import { Button } from '@ctw/shared/components/button';
import { Tooltip } from '@ctw/shared/components/tooltip';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faTrash, faTrashUndo } from '@fortawesome/pro-duotone-svg-icons';
import { faFileLines } from '@fortawesome/pro-regular-svg-icons';
// import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createContext, type PropsWithChildren, type ReactNode, useContext } from 'react';
import type { MergeExclusive } from 'type-fest';

export interface PatientFactGroup {
  label: string;
  facts: Array<PatientFact>;
}

export type PatientFactCitationUnavailableReason = 'loading' | 'error';

export interface PatientFactCitation {
  // Whether the cited source can be opened (document viewer or resource drawer).
  // When false, the icon is shown but greyed out and not selectable.
  isOpenable: boolean;
  // Display name of the cited document or resource, shown on hover when openable.
  label?: string;
  // Why an unopenable citation can't be opened yet. Omitted when resolution
  // finished and found nothing to open.
  unavailableReason?: PatientFactCitationUnavailableReason;
}

const UNAVAILABLE_CITATION_TEXT: Record<PatientFactCitationUnavailableReason | 'none', string> = {
  loading: 'Loading source',
  error: 'Source could not be loaded',
  none: 'Source not available',
};

interface PatientFactNotFound {
  id: string;
  label: string;
}

interface PatientFactFound {
  id: string;
  label: string;
  value: ReactNode;
  citation?: PatientFactCitation;
}

export type PatientFact = MergeExclusive<PatientFactFound, PatientFactNotFound>;

const isFoundFact = (fact: PatientFact): fact is PatientFactFound => Object.hasOwn(fact, 'value');
const isNotFoundFact = (fact: PatientFact): fact is PatientFactNotFound =>
  !Object.hasOwn(fact, 'value');

export interface PatientFactCallbackArgs {
  fact: PatientFact;
}

interface PatientFactExtractionGroupedTableState {
  isRedacted?: (fact: PatientFact) => boolean;
  onRedactFact?: (args: PatientFactCallbackArgs) => void;
  onOpenCitation?: (args: PatientFactCallbackArgs) => void;
  showRemoveAction?: boolean;
}

const PatientFactExtractionGroupedTableContext = createContext<
  PatientFactExtractionGroupedTableState | undefined
>(undefined);

interface PatientFactExtractionGroupedTableProviderProps
  extends PropsWithChildren,
    PatientFactExtractionGroupedTableState {}

const PatientFactExtractionGroupedTableProvider = ({
  children,
  ...tableState
}: PatientFactExtractionGroupedTableProviderProps) => (
  <PatientFactExtractionGroupedTableContext.Provider value={tableState}>
    {children}
  </PatientFactExtractionGroupedTableContext.Provider>
);

const usePatientFactExtractionGroupedTableState = () => {
  const context = useContext(PatientFactExtractionGroupedTableContext);

  if (!context) {
    throw new Error(
      'usePatientFactExtractionGroupedTableState must be used within a PatientFactExtractionGroupedTable',
    );
  }

  return context;
};

export interface PatientFactExtractionGroupedTableProps
  extends PatientFactExtractionGroupedTableState {
  factGroups: Array<PatientFactGroup>;
}

export const PatientFactExtractionGroupedTable = ({
  factGroups,
  ...tableState
}: PatientFactExtractionGroupedTableProps) => (
  <PatientFactExtractionGroupedTableProvider {...tableState}>
    <div
      className={tw`divide-y divide-divider-main overflow-hidden rounded-md border border-border-main/80`}
    >
      {factGroups.map((factGroup) => (
        <PatientFactExtractionTableGroup key={factGroup.label} factGroup={factGroup} />
      ))}
    </div>
  </PatientFactExtractionGroupedTableProvider>
);

interface PatientFactExtractionTableGroupProps {
  factGroup: PatientFactGroup;
}

const PatientFactExtractionTableGroup = ({ factGroup }: PatientFactExtractionTableGroupProps) => (
  <div>
    <div
      className={tw`border-divider-main border-b bg-content-subtle/10 px-4 py-2 font-bold text-content-main/65 text-xs uppercase tracking-wide`}
    >
      {factGroup.label}
    </div>
    <div className={tw`divide-y divide-divider-main`}>
      {factGroup.facts.map((fact) => (
        <PatientFactExtractionTableItem key={fact.id} fact={fact} />
      ))}
    </div>
  </div>
);

interface PatientFactExtractionTableItemProps {
  fact: PatientFact;
}

const PatientFactExtractionTableItem = ({ fact }: PatientFactExtractionTableItemProps) => {
  const {
    isRedacted,
    onRedactFact,
    onOpenCitation,
    showRemoveAction = false,
  } = usePatientFactExtractionGroupedTableState();
  const redacted = isRedacted?.(fact) ?? false;
  const citation = isFoundFact(fact) ? fact.citation : undefined;

  const handleOpenCitation = () => {
    onOpenCitation?.({ fact });
  };

  const handleToggleRedaction = () => {
    onRedactFact?.({ fact });
  };

  const citationOpenable = Boolean(citation?.isOpenable);
  const citationText = citationOpenable
    ? 'View source'
    : UNAVAILABLE_CITATION_TEXT[citation?.unavailableReason ?? 'none'];

  const citationButton = citation ? (
    <Button
      className={twx(
        'group/citation-button flex aspect-square items-center justify-center rounded-md p-1 transition-all duration-150',
        {
          'active:!scale-[.99] drop-shadow-sm hover:bg-info-main/10': citationOpenable,
          'pointer-events-none cursor-not-allowed opacity-40': !citationOpenable,
        },
      )}
      type="button"
      variant="unstyled"
      onClick={citationOpenable ? handleOpenCitation : undefined}
      ariaLabel={citationText}
    >
      <FontAwesomeIcon
        icon={faFileLines}
        className={twx('h-3.5 w-3.5 text-content-icon', {
          'group-hover/citation-button:text-info-text': citationOpenable,
        })}
      />
    </Button>
  ) : null;

  const citationIcon = citation ? (
    <Tooltip
      id={`fact-citation-${fact.id}`}
      place="left"
      content={citationOpenable ? (citation.label ?? citationText) : citationText}
    >
      {citationButton}
    </Tooltip>
  ) : (
    citationButton
  );

  return (
    <div
      className={twx(
        'group/fact-row flex min-h-[3rem] items-center justify-between gap-4 px-4 py-2.5 hover:bg-content-disabled/5',
        {
          'bg-caution-background/70': !redacted && isNotFoundFact(fact),
          'bg-error-background/30': redacted,
        },
      )}
    >
      <div className={tw`flex items-center gap-4`}>
        <div
          className={tw`flex shrink-0 basis-48 items-center gap-0.5 font-medium text-content-subtle text-sm`}
        >
          <div
            className={twx('break-words font-medium text-content-subtle text-sm', {
              'line-through': redacted,
            })}
          >
            {fact.label}
          </div>
        </div>
        <div className={tw`min-w-0 flex-1 break-words font-medium text-sm`}>
          {isFoundFact(fact) ? (
            <span
              // Multi-entry values (infection roll-ups, lab histories) arrive
              // newline-delimited from the extraction API; render each on its own line.
              className={twx('whitespace-pre-line', {
                'text-content-main': !redacted,
                'text-content-subtle line-through': redacted,
              })}
            >
              {fact.value}
            </span>
          ) : (
            <span
              className={twx('text-content-subtle', {
                'line-through': redacted,
              })}
            >
              <span aria-hidden="true">&mdash;</span>
              <span className={tw`sr-only`}>Not found</span>
            </span>
          )}
        </div>
      </div>
      <div
        className={tw`flex items-center opacity-0 group-hover/fact-row:opacity-100 has-[:focus-visible]:opacity-100`}
      >
        {citationIcon}
        {showRemoveAction && (
          <Button
            className={twx(
              'group/delete-button active:!scale-[.90] flex aspect-square items-center justify-center rounded-md p-1 drop-shadow-sm transition-all duration-150 hover:shadow-sm',
              {
                'hover:bg-error-main/10': !redacted,
                'hover:bg-success-main/10': redacted,
              },
            )}
            type="button"
            variant="unstyled"
            onClick={handleToggleRedaction}
            ariaLabel={redacted ? 'Restore fact' : 'Remove fact'}
          >
            <FontAwesomeIcon
              icon={redacted ? faTrashUndo : faTrash}
              className={twx('h-3.5 w-3.5 text-content-icon', {
                'group-hover/delete-button:text-error-text': !redacted,
                'group-hover/delete-button:text-success-text': redacted,
              })}
            />
          </Button>
        )}
      </div>
    </div>
  );
};
